import React, { useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import { Redirect, Route, Switch } from 'react-router'
import ProductLessons from 'components/ProductLessons'

import { CreateClassModals } from './CreateClassModals'
import { LessonBreadcrumbs } from './LessonBreadcrumbs'
import { Wrapper } from './component.styles'
import { useAutoOpenTrigger } from './hooks'
import { getLessonPageViewModel } from './data'

const LessonsComponent = props => {
  const {
    classes = [],
    contentfulCourses,
    courseLinks,
    courseManager,
    currentSiteV4,
    goToDashboard,
    lessonCompletionGoalValue,
    match,
    onClassClick,
    onComponentClick,
    onCourseClick,
    productName,
    productTabTitle,
    showSmallGroupCreation = true,
    // CreateCourse props
    lms,
    pathName,
    userAccess,
    userContext,
    userProfile,
    createCourseInstanceV2ProgramFlex,
    createCourseInstanceByCourse,
    deletingCourseCreatedStatus,
    deleteCourseInstance,
    fetchInstructorData,
    updateCourseInstance,
  } = props || {}

  const createCourseTrigger = useAutoOpenTrigger()
  const createSmallGroupTrigger = useAutoOpenTrigger()
  const { baseUrl, productTitle, tabTitle, withVerb } = useMemo(
    () => getLessonPageViewModel({ match, productName, productTabTitle }),
    [match, productName, productTabTitle],
  )

  useEffect(() => {
    document.title = tabTitle
  }, [tabTitle])

  return (
    <Wrapper>
      <Switch>
        {/* Previewing or teaching a with nothing specified; go to just /lessons */}
        <Redirect exact from={`${withVerb}`} to={`${baseUrl}`} />
        {/* Show courses to preview and classes to teach (if there are any) */}
        <Route
          path={baseUrl}
          render={() => (
            <>
              <LessonBreadcrumbs goToDashboard={goToDashboard} />

              <ProductLessons
                classes={classes}
                courseLinks={courseLinks}
                courseManager={courseManager}
                currentSite={currentSiteV4}
                lessonCompletionGoalValue={lessonCompletionGoalValue}
                onClassClick={onClassClick}
                onComponentClick={onComponentClick}
                onCourseClick={onCourseClick}
                onCreateClass={createCourseTrigger.open}
                onCreateSmallGroup={
                  showSmallGroupCreation
                    ? createSmallGroupTrigger.open
                    : undefined
                }
                program={productName}
                showSmallGroupCreation={showSmallGroupCreation}
                title={productTitle}
              />
              <CreateClassModals
                contentfulCourses={contentfulCourses}
                createCourseAutoOpen={createCourseTrigger.autoOpen}
                createCourseInstanceByCourse={createCourseInstanceByCourse}
                createCourseInstanceV2ProgramFlex={
                  createCourseInstanceV2ProgramFlex
                }
                createSmallGroupAutoOpen={createSmallGroupTrigger.autoOpen}
                deleteCourseInstance={deleteCourseInstance}
                deletingCourseCreatedStatus={deletingCourseCreatedStatus}
                fetchInstructorData={fetchInstructorData}
                lms={lms}
                pathName={pathName}
                showSmallGroupCreation={showSmallGroupCreation}
                updateCourseInstance={updateCourseInstance}
                userAccess={userAccess}
                userContext={userContext}
                userProfile={userProfile}
              />
            </>
          )}
        />
      </Switch>
    </Wrapper>
  )
}

LessonsComponent.propTypes = {
  classes: PropTypes.arrayOf(
    PropTypes.shape({
      class_title: PropTypes.string,
      completeLessons: PropTypes.number,
      contentfulID: PropTypes.string,
      course_name: PropTypes.string,
      instance: PropTypes.string,
      registration_id: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
      ]),
      scoId: PropTypes.number,
      title: PropTypes.string,
      totalLessons: PropTypes.number,
    }),
  ),
  contentfulCourses: PropTypes.arrayOf(
    PropTypes.shape({
      displayTitle: PropTypes.string,
    }),
  ).isRequired,
  courseLinks: PropTypes.arrayOf(
    PropTypes.shape({
      slug: PropTypes.string,
      title: PropTypes.string,
    }),
  ).isRequired,
  courseManager: PropTypes.object,
  createCourseInstanceByCourse: PropTypes.func,
  createCourseInstanceV2ProgramFlex: PropTypes.func,
  currentSiteV4: PropTypes.object,
  deleteCourseInstance: PropTypes.func,
  deletingCourseCreatedStatus: PropTypes.func,
  fetchInstructorData: PropTypes.func,
  goToDashboard: PropTypes.func.isRequired,
  lms: PropTypes.object,
  match: PropTypes.shape({
    url: PropTypes.string,
    path: PropTypes.string,
    params: PropTypes.shape({
      productName: PropTypes.string,
    }),
  }).isRequired,
  onClassClick: PropTypes.func,
  onComponentClick: PropTypes.func,
  onCourseClick: PropTypes.func.isRequired,
  pathName: PropTypes.string,
  productName: PropTypes.string,
  productTabTitle: PropTypes.string,
  showSmallGroupCreation: PropTypes.bool,
  updateCourseInstance: PropTypes.func,
  userAccess: PropTypes.object,
  userContext: PropTypes.object,
  userProfile: PropTypes.object,
}

export default LessonsComponent
